import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../shared/helpers/icons';
import { Building } from '../../shared/models/building';
import { BuildingService } from '../../shared/services/building.service';
import { BuildingContextService } from '../../shared/services/building-context.service';

const formatBuilding = (b: Building) => `${b.name} #${b.id}`;

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './navbar.component.html'
})
export class NavbarComponent implements OnInit {
  icons = Icons;

  buildings = signal<Building[]>([]);
  query = signal('');
  dropdownOpen = signal(false);
  loading = signal(false);

  filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const all = this.buildings();
    if (!q) return all;
    return all.filter(b => b.name.toLowerCase().includes(q));
  });

  constructor(
    private buildingService: BuildingService,
    public buildingContext: BuildingContextService
  ) {
    const initial = this.buildingContext.selected();
    if (initial) this.query.set(formatBuilding(initial));
    effect(() => {
      const current = this.buildingContext.selected();
      if (this.dropdownOpen()) return;
      this.query.set(current ? formatBuilding(current) : '');
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loading.set(true);
    this.buildingService.list().subscribe({
      next: rows => {
        this.buildings.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  openDropdown(): void {
    this.dropdownOpen.set(true);
    // Empty the query when opening so all options show;
    this.query.set('');
  }

  closeDropdownSoon(): void {
    setTimeout(() => {
      this.dropdownOpen.set(false);
      const current = this.buildingContext.selected();
      this.query.set(current ? formatBuilding(current) : '');
    }, 150);
  }

  pick(building: Building): void {
    this.buildingContext.set(building);
    this.query.set(formatBuilding(building));
    this.dropdownOpen.set(false);
  }

  clear(): void {
    this.buildingContext.clear();
    this.query.set('');
  }
}
